import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import authAxios from '../utils/authAxios';
import StandardTableLayout from '../styles/StandardTableLayout';
import { Input, Select, Button, DangerButton } from '../styles/TableStyles';
import TypeaheadSelect from '../components/TypeaheadSelect';
import ChargeByStyleDetailPanel from '../components/ChargeByStyleDetailPanel';

const initialFormState = {
  commodityUniqueId: '',
  bagType: '',
  productType: 'Bag',
  isTWB: false,
  count: '',
  bagWeight: '',
  bulkWeight: '',
  tihi: '',
};

const columns = [
  { key: 'Commodity',      label: 'Commodity' },
  { key: 'BagType',        label: 'Bag Type' },
  { key: 'ProductType',    label: 'Product Type' },
  { key: 'IsTWB',          label: 'TWB' },
  { key: 'Count',          label: 'Count' },
  { key: 'BagWeight',      label: 'Bag Weight' },
  { key: 'BulkWeight',     label: 'Bulk Weight' },
  { key: 'CostConversion', label: 'Cost Conv.' },
  { key: 'PalletsChg',     label: 'Pallets Chg.' },
  { key: 'MarketingChg',   label: 'Marketing Chg.' },
  { key: 'PackingChg',     label: 'Packing Chg.' },
  { key: 'ImportChg',      label: 'Import Chg.' },
  { key: 'TotalChg',       label: 'Total Chg.' },
  { key: 'TiHi',           label: 'Ti × Hi' },
];

const CURRENCY_KEYS = new Set(['PalletsChg', 'MarketingChg', 'PackingChg', 'ImportChg', 'TotalChg']);

const formatCurrency = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : '';
};

const formatBagWeight = (v) => {
  if (v == null) return '';
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toString() : '';
};

export default function ChargeByStylePage() {
  const [entries, setEntries] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [packTypes, setPackTypes] = useState([]);
  const [bagCosts, setBagCosts] = useState([]);
  const [formData, setFormData] = useState(initialFormState);
  const [showForm, setShowForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [submitting, setSubmitting] = useState(false);

  const fetchEntries = useCallback(async () => {
    try {
      const res = await authAxios.get('/api/chargebystyle');
      const data = Array.isArray(res.data) ? res.data : [];
      setEntries(data);
      return data;
    } catch (err) {
      console.error('Failed to fetch ChargeByStyle entries:', err);
      return [];
    }
  }, []);

  const fetchCommodities = useCallback(async () => {
    try {
      // All commodities (not just those with existing charges) so a fresh DB can bootstrap.
      const res = await authAxios.get('/api/commodities/dropdown');
      setCommodities(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch commodities:', err);
    }
  }, []);

  const fetchPackTypes = useCallback(async () => {
    try {
      const res = await authAxios.get('/api/packtypes');
      setPackTypes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch pack types:', err);
    }
  }, []);

  const fetchBagCosts = useCallback(async () => {
    try {
      const res = await authAxios.get('/api/bagcosts');
      setBagCosts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch bag costs:', err);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
    fetchCommodities();
    fetchPackTypes();
    fetchBagCosts();
  }, [fetchEntries, fetchCommodities, fetchPackTypes, fetchBagCosts]);

  const commodityOptions = useMemo(
    () => commodities.map(c => ({ value: c.UniqueID, label: c.Commodity })),
    [commodities]
  );
  const groups = useMemo(
    () => [...new Set(packTypes.map(p => p.Type).filter(Boolean))].sort(),
    [packTypes]
  );
  const styleNames = useMemo(
    () => [...new Set(packTypes.map(p => p.Style).filter(Boolean))].sort(),
    [packTypes]
  );
  const packByStyle = useMemo(() => {
    const m = new Map();
    packTypes.forEach(p => { if (p.Style) m.set(p.Style, p); });
    return m;
  }, [packTypes]);
  const bagCostByStyle = useMemo(() => {
    const m = new Map();
    bagCosts.forEach(b => { if (b.BagName) m.set(b.BagName, b); });
    return m;
  }, [bagCosts]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'productType') {
        if (value !== 'Bag') { next.count = ''; next.bagWeight = ''; }
        else { next.bulkWeight = ''; }
      }
      return next;
    });
  };

  const handleCommodityChange = (uniqueId) => {
    setFormData(prev => ({ ...prev, commodityUniqueId: String(uniqueId) }));
  };

  // Picking a Style prefills Product Type (+ Count/Bag Weight for Bag) — all still overridable.
  // Picking a group (Type) leaves the rest blank for manual entry.
  const handlePackChange = (e) => {
    const value = e.target.value;
    setFormData(prev => {
      const next = { ...prev, bagType: value };
      const pt = packByStyle.get(value);
      if (pt) {
        // TWB is now an independent flag (Decision #7). A legacy pack typed 'TWB' prefills as
        // Bulk + TWB-checked (overridable); otherwise the pack's Bag/Bulk type carries through.
        if (pt.ProductType === 'TWB') { next.productType = 'Bulk'; next.isTWB = true; }
        else if (pt.ProductType) { next.productType = pt.ProductType; }
        if (next.productType === 'Bag') {
          const bc = bagCostByStyle.get(value);
          if (bc) {
            if (bc.Count != null) next.count = String(bc.Count);
            if (bc.BagWeight != null) next.bagWeight = parseFloat(bc.BagWeight).toString();
          }
          next.bulkWeight = '';
        } else {
          next.count = '';
          next.bagWeight = '';
        }
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (submitting) return;

    const cid = parseInt(formData.commodityUniqueId, 10);
    if (!Number.isInteger(cid) || cid <= 0) { toast.error('Commodity is required'); return; }
    if (!formData.bagType) { toast.error('Bag Type is required'); return; }
    if (formData.productType === 'Bag' && (!formData.count || !formData.bagWeight)) {
      toast.error('Count and Bag Weight are required for Bag product type'); return;
    }
    if (formData.productType !== 'Bag' && !formData.bulkWeight) {
      toast.error('Bulk Weight is required for Bulk product type'); return;
    }

    setSubmitting(true);
    try {
      const payload = {
        commodityUniqueId: cid,
        bagType: formData.bagType,
        productType: formData.productType,
        isTWB:      !!formData.isTWB,
        count:      formData.productType === 'Bag' ? parseInt(formData.count, 10) || 0 : 0,
        bagWeight:  formData.productType === 'Bag' ? parseFloat(formData.bagWeight) || 0 : 0,
        bulkWeight: formData.productType !== 'Bag' ? parseFloat(formData.bulkWeight) || 0 : 0,
        tihi:       formData.tihi?.trim() || null,
      };
      await authAxios.post('/api/chargebystyle', payload);
      setFormData(initialFormState);
      setShowForm(false);
      await fetchEntries();
      toast.success('Charge created');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save entry';
      toast.error(msg);
      console.error('ChargeByStyle save failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const filteredAndSorted = useMemo(() => {
    const lower = String(searchQuery || '').toLowerCase();
    const filtered = !lower
      ? entries
      : entries.filter(row =>
          Object.values(row).some(v => String(v ?? '').toLowerCase().includes(lower))
        );
    if (!sortConfig.key) return filtered;
    const sortable = [...filtered];
    sortable.sort((a, b) => {
      const va = a[sortConfig.key];
      const vb = b[sortConfig.key];
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortConfig.direction === 'asc' ? va - vb : vb - va;
      }
      return sortConfig.direction === 'asc'
        ? String(va ?? '').localeCompare(String(vb ?? ''))
        : String(vb ?? '').localeCompare(String(va ?? ''));
    });
    return sortable;
  }, [entries, sortConfig, searchQuery]);

  const handleUpdated = useCallback(async () => {
    const fresh = await fetchEntries();
    if (selectedItem) {
      const updated = fresh.find(r => r.UniqueID === selectedItem.UniqueID);
      setSelectedItem(updated || null);
    }
  }, [fetchEntries, selectedItem]);

  const handleDeleted = useCallback(async () => {
    setSelectedItem(null);
    await fetchEntries();
  }, [fetchEntries]);

  const renderFormCell = (key) => {
    if (key === 'Commodity') {
      return (
        <TypeaheadSelect
          value={formData.commodityUniqueId}
          onChange={handleCommodityChange}
          options={commodityOptions}
          placeholder="Select Commodity"
          ariaLabel="Commodity"
          size="md"
        />
      );
    }
    if (key === 'BagType') {
      return (
        <Select name="bagType" aria-label="Pack type" value={formData.bagType} onChange={handlePackChange}>
          <option value="">Select pack…</option>
          <optgroup label="Groups">
            {groups.map((g) => <option key={`g-${g}`} value={g}>{g}</option>)}
          </optgroup>
          <optgroup label="Styles">
            {styleNames.map((s) => <option key={`s-${s}`} value={s}>{s}</option>)}
          </optgroup>
        </Select>
      );
    }
    if (key === 'ProductType') {
      return (
        <Select name="productType" value={formData.productType} onChange={handleChange}>
          <option value="Bag">Bag</option>
          <option value="Bulk">Bulk</option>
        </Select>
      );
    }
    if (key === 'IsTWB') {
      return (
        <input
          type="checkbox"
          name="isTWB"
          aria-label="TWB"
          checked={!!formData.isTWB}
          onChange={handleChange}
        />
      );
    }
    if (key === 'Count') {
      if (formData.productType !== 'Bag') return null;
      return (
        <Input type="number" name="count" min="1" step="1" placeholder="ct" value={formData.count} onChange={handleChange} />
      );
    }
    if (key === 'BagWeight') {
      if (formData.productType !== 'Bag') return null;
      return (
        <Input type="number" name="bagWeight" min="0" step="0.01" placeholder="lb" value={formData.bagWeight} onChange={handleChange} />
      );
    }
    if (key === 'BulkWeight') {
      if (formData.productType === 'Bag') return null;
      return (
        <Input type="number" name="bulkWeight" min="0" step="any" value={formData.bulkWeight} onChange={handleChange} />
      );
    }
    if (key === 'TiHi') {
      return (
        <Input type="text" name="tihi" placeholder="0 x 0" value={formData.tihi} onChange={handleChange} />
      );
    }
    return null; // computed columns are absent from the form
  };

  const renderDataCell = (key, row) => {
    if (!row) return '';
    if (key === 'IsTWB') {
      return (row.IsTWB === true || row.IsTWB === 1) ? 'Yes' : '';
    }
    if (key === 'TiHi') {
      return row.TiHi == null ? '' : String(row.TiHi);
    }
    if (key === 'CostConversion') {
      const n = parseFloat(row.CostConversion);
      return Number.isFinite(n) ? n.toFixed(4) : '';
    }
    if (key === 'BagWeight' || key === 'BulkWeight') {
      return formatBagWeight(row[key]);
    }
    if (CURRENCY_KEYS.has(key)) {
      return formatCurrency(row[key]);
    }
    const v = row[key];
    return v == null ? '' : String(v);
  };

  const renderCell = (key, dataOrForm, handleChangeArg) => {
    if (typeof handleChangeArg === 'function') return renderFormCell(key);
    return renderDataCell(key, dataOrForm);
  };

  const handleToggleForm = () => {
    if (showForm) setFormData(initialFormState);
    setShowForm(s => !s);
  };

  const handleCancelForm = () => {
    setFormData(initialFormState);
    setShowForm(false);
  };

  return (
    <>
      <StandardTableLayout
        title="Charge By Style"
        columns={columns}
        rows={filteredAndSorted}
        sortConfig={sortConfig}
        onSort={handleSort}
        searchValue={searchQuery}
        onSearchChange={(e) => setSearchQuery(e.target.value)}
        showForm={showForm}
        onToggleForm={handleToggleForm}
        formData={formData}
        handleChange={handleChange}
        onSubmit={handleSubmit}
        renderCell={renderCell}
        hideActionsColumn
        getRowProps={(row) => ({
          onClick: () => setSelectedItem(row),
          style: { cursor: 'pointer' },
        })}
        submitting={submitting}
      >
        {showForm && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <DangerButton type="button" onClick={handleCancelForm} disabled={submitting}>Cancel</DangerButton>
            <Button type="button" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </StandardTableLayout>

      {selectedItem && (
        <ChargeByStyleDetailPanel
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdated={handleUpdated}
          onDeleted={handleDeleted}
        />
      )}
    </>
  );
}
